import {
  Component,
  HostBinding,
  Input,
  OnInit,
  OnChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { NavItem } from './nav-item';
import { Router } from '@angular/router';
import { NavService } from '../../../../../services/nav.service';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { CoreService } from 'src/app/services/core.service';
import { SetupProgressService } from 'src/app/services/setup-progress.service';
import { I18nService } from 'src/app/i18n/i18n.service';

@Component({
  selector: 'app-nav-item',
  imports: [TranslateModule, TablerIconsModule, MaterialModule, CommonModule],
  templateUrl: './nav-item.component.html',
  styles: [`
    .menu-locked { opacity: .55; }
    .menu-lock-icon { width: 16px; height: 16px; margin-left: auto; flex: 0 0 16px; }
  `],
  animations: [
    trigger('indicatorRotate', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(180deg)' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4,0.0,0.2,1)')),
    ]),
  ]
})
export class AppNavItemComponent implements OnChanges {
  @Output() toggleMobileLink: any = new EventEmitter<void>();
  @Output() notify: EventEmitter<boolean> = new EventEmitter<boolean>();

  expanded: any = false;
  disabled: any = false;
  twoLines: any = false;
  @HostBinding('attr.aria-expanded') ariaExpanded = this.expanded;
  @Input() item: NavItem | any;
  @Input() depth: any;

  constructor(public navService: NavService, public router: Router, private core: CoreService,
    private progress: SetupProgressService, private i18n: I18nService) {
    if (this.depth === undefined) {
      this.depth = 0;
    }
  }

  ngOnChanges() {
    if (!this.item) return;

    const role = this.core.getRole();

    // 🔒 Ocultar por rol si no está permitido
    if (this.item.allowedRoles && !this.item.allowedRoles.includes(role)) {
      this.item = null;
      return;
    }

    // 🔒 Ocultar por condición especial: companyOwner sin compañía, o sin tarjeta / con la suscripción cancelada
    if (this.core.ownerSignupPending()) {
      this.item = null;
      return;
    }


    // 📌 Expandir menú si la URL coincide
    const url = this.navService.currentUrl();
    if (this.item.route && url) {
      this.expanded = url.indexOf(`/${this.item.route}`) === 0;
      this.ariaExpanded = this.expanded;
    }
  }

  /** Companies that signed up by themselves: the links to TTO's own agents (Smart Recruiting, Smart VPOD) are not theirs. */
  get hiddenForCompany(): boolean {
    // "Getting started" es solo para las compañías que se registraron solas (TTO y las de antes no lo ven).
    if (this.item?.route === '/apps/company-setup') return !this.progress.managed();
    return !!this.item?.external && this.progress.managed();
  }

  /** The setup step this item waits for (companies that signed up by themselves), or null when open. */
  get lockedBy() {
    return this.item?.route ? this.progress.lockedBy(this.item.route) : null;
  }

  get lockTitle(): string | null {
    const step = this.lockedBy;
    if (!step) return null;
    const key = 'su.step.' + step.key;
    const name = this.i18n.t(key);
    return this.i18n.t('menu.locked', { step: name === key ? step.title : name });
  }

  onItemSelected(item: NavItem) {
    // Todavía cerrado: lleva a la lista de pasos, donde está lo que falta.
    if (this.lockedBy) {
      this.router.navigate(['/apps/company-setup']);
      return;
    }
    if (!item.children || !item.children.length) {
      this.router.navigate([item.route]);

    }
    if (item.children && item.children.length) {
      this.expanded = !this.expanded;
    }
    //scroll
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    if (!this.expanded) {
      if (window.innerWidth < 1024) {
        this.notify.emit();
      }
    }
  }

  onSubItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      if (this.expanded && window.innerWidth < 1024) {
        this.notify.emit();
      }
    }
  }

  getFilteredChildren(): NavItem[] {
    const role = this.core.getRole();

    // 🔒 Si es CompanyOwner y no terminó el registro (sin compañía o sin tarjeta), ocultar el menú
    if (this.core.ownerSignupPending()) {
      return [];
    }

    // 🔍 Filtrar hijos según los roles permitidos
    return this.item?.children?.filter((child: NavItem) =>
      !child.allowedRoles || child.allowedRoles.includes(role)
    ) || [];
  }
}
